// Drawer controller
// Handles drawer slide-in/out with backdrop
// Adapted from railsblocks.com/docs/drawer

import { Controller } from "@hotwired/stimulus"

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default class extends Controller {
  static targets = ["backdrop", "panel"]
  static values = {
    open: Boolean,
    position: { type: String, default: "right" },
    dismissible: { type: Boolean, default: true },
    stackLevel: Number,
  }

  // How far an open drawer slides aside when a drawer opens on top of it, so the
  // parent stays partly visible instead of being covered exactly — the nested
  // panel is the same width and would otherwise hide it completely, leaving the
  // user with no sight of the form they were filling in. CONEK-6530.
  //
  // `-translate-x-full` is 100% of the panel's OWN width, so the parent slides
  // exactly one drawer-width whatever that width is. This was a fixed
  // -translate-x-112 (28rem/448px), which is identical while the panel is 448px
  // but overshoots once it is viewport-width on a phone — there the parent was
  // pushed a further 73px past the screen edge instead of sitting flush against
  // the panel above it. CONEK-8293. Mirrors the React side's -translate-x-full!.
  static SHIFT_CLASS = "-translate-x-full"

  connect() {
    this._previouslyFocused = null

    if (this.openValue) {
      this.show()
    }

    // A drawer opened from this one is a sibling in the DOM, so the shift is
    // coordinated through window events rather than parent/child nesting.
    this._stackHandler = this._onStackChange.bind(this)
    window.addEventListener("drawer:open", this._stackHandler)
    window.addEventListener("drawer:close", this._stackHandler)

    // Listen for escape key
    this.escapeHandler = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.escapeHandler)

    // Focus trap handler
    this._trapFocusHandler = this._trapFocus.bind(this)
    this.element.addEventListener("keydown", this._trapFocusHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.escapeHandler)
    this.element.removeEventListener("keydown", this._trapFocusHandler)
    window.removeEventListener("drawer:open", this._stackHandler)
    window.removeEventListener("drawer:close", this._stackHandler)
    document.body.style.overflow = ""
  }

  // Slide aside while THIS drawer's own child is open; slide back when it goes.
  //
  // Keyed on the child naming its parent (ui--drawer-opener records the drawer its
  // trigger sits inside), not on the stack level alone: a bare level comparison
  // would also fire for an unrelated drawer elsewhere on the page that happened to
  // carry a lower one. The level is still checked, so a child can never push its
  // own parent the wrong way.
  _onStackChange(event) {
    if (event.detail?.openedFrom !== this.element.id) return

    const level = event.detail?.stackLevel
    if (typeof level !== "number" || level <= this.stackLevelValue) return
    if (this.element.classList.contains("hidden")) return
    // Only the side drawers slide; a top/bottom sheet has nothing to move aside.
    if (!["left", "right"].includes(this.positionValue)) return

    this.panelTarget.classList.toggle(
      this.constructor.SHIFT_CLASS,
      event.type === "drawer:open",
    )
  }

  show() {
    // Store the element that had focus before opening
    this._previouslyFocused = document.activeElement

    this.element.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    // Remove transform to slide in
    requestAnimationFrame(() => {
      this.panelTarget.classList.remove(this.getTransformClass())
      this.panelTarget.classList.remove(this.constructor.SHIFT_CLASS)

      // Set initial focus: [autofocus], or first focusable in panel
      const autofocusEl = this.panelTarget.querySelector("[autofocus]")
      if (autofocusEl) {
        autofocusEl.focus()
      } else {
        const firstFocusable =
          this.panelTarget.querySelector(FOCUSABLE_SELECTOR)
        if (firstFocusable) {
          firstFocusable.focus()
        }
      }
    })

    // Dispatch open event
    this.element.dispatchEvent(
      new CustomEvent("drawer:open", {
        bubbles: true,
        detail: {
          stackLevel: this.stackLevelValue,
          openedFrom: this.element.dataset.openedFrom,
        },
      }),
    )
  }

  close(_event) {
    if (!this.dismissibleValue) return

    // Add transform to slide out
    this.panelTarget.classList.add(this.getTransformClass())

    setTimeout(() => {
      this.element.classList.add("hidden")
      document.body.style.overflow = ""

      // Return focus to the element that triggered the drawer
      if (
        this._previouslyFocused &&
        typeof this._previouslyFocused.focus === "function"
      ) {
        this._previouslyFocused.focus()
        this._previouslyFocused = null
      }

      // Dispatch close event
      this.element.dispatchEvent(
        new CustomEvent("drawer:close", {
          bubbles: true,
          detail: {
            stackLevel: this.stackLevelValue,
            openedFrom: this.element.dataset.openedFrom,
          },
        }),
      )
    }, 500) // matches the panel's `duration-500` slide-out transition
  }

  handleEscape(event) {
    if (event.key === "Escape" && !this.element.classList.contains("hidden")) {
      this.close()
    }
  }

  // Trap focus within the drawer panel when Tab is pressed
  _trapFocus(event) {
    if (event.key !== "Tab" || this.element.classList.contains("hidden")) return

    const focusableElements = Array.from(
      this.panelTarget.querySelectorAll(FOCUSABLE_SELECTOR),
    )
    if (focusableElements.length === 0) return

    const firstFocusable = focusableElements[0]
    const lastFocusable = focusableElements[focusableElements.length - 1]

    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        event.preventDefault()
        lastFocusable.focus()
      }
    } else {
      if (document.activeElement === lastFocusable) {
        event.preventDefault()
        firstFocusable.focus()
      }
    }
  }

  getTransformClass() {
    const transforms = {
      left: "-translate-x-full",
      right: "translate-x-full",
      top: "-translate-y-full",
      bottom: "translate-y-full",
    }
    return transforms[this.positionValue] || transforms.right
  }
}
